//
// Utility extensions and functions
//

import { IStringLocalizer } from "./IStringLocalizer";

// ===============================================================================

declare global {
    interface Array<T> {
        containsAnyOf(this: T[], suppliedTargets: T[]): boolean;
        take(this: Array<T>, count: number): Array<T>;
        skip(this: Array<T>, count: number): Array<T>;
        distinct(this: Array<T>): Array<T>;
        distinctByStringProjection(this: Array<T>, projection: (obj: T) => string): Array<T>;
        firstOrDefault(this: Array<T>, predicate: (obj: T) => boolean): (T | null);
        first(this: Array<T>, predicate: (obj: T) => boolean): T;
        selectMany<TSelector>(this: Array<T>, selector: (obj: T) => Array<TSelector>): Array<TSelector>;
        orderBy(this: Array<T>, selector: (obj: T) => string): Array<T>;
        orderByDescending(this: Array<T>, selector: (obj: T) => string): Array<T>;
        truncateAndJoinListOfStringsWithCountDescription(this: Array<string>, localizer: IStringLocalizer, truncateThreshold: number, joinString: string): string;
        equalTo(this: Array<string>, a2: Array<string>, enforceOrginalOrder?: boolean): boolean;
        getDuplicates(this: Array<string | number>): Array<string | number>;
        toDictionary(this: Array<T>, keySelector: (obj: T) => string): { [key: string]: T };
    }

    interface String {
        coalesceToMaxSizeAndIndicateTruncationWithEllipsis(this: string, maxSize: number): string;
        emptyCoalesce(this: string, coalescedValue: string): string;
        trimStartString(this: string, startString: string): string;
        trimEndString(this: string, endString: string): string;
        trimChar(this: string, char: string): string;
        trimChars(this: string, chars: string[]): string;
        toDoubleOrDefault(this: string, defaultToUse: number): number;
        isDigit(this: string): boolean;
        isWhitespace(this: string): boolean;
    }

    interface Number {
        roundBy(this: number, decimalPts: number): number;
    }
}

// ===============================================================================

//
// Array extensions
//

Array.prototype.containsAnyOf = function <T>(this: T[], suppliedTargets: T[]): boolean {
    return this.some(item => suppliedTargets.includes(item));
}

// ===============================================================================

Array.prototype.take = function <T>(this: T[], count: number) {
    return this.slice(0, count);
};

// ===============================================================================

Array.prototype.skip = function <T>(this: T[], count: number) {
    return this.slice(count);
};

// ===============================================================================

Array.prototype.distinct = function <T>(this: Array<T>): Array<T> {
    return Array.from(new Set<T>(this));
};

// ===============================================================================

Array.prototype.distinctByStringProjection = function <T>(this: Array<T>, projection: (obj: T) => string): Array<T> {
    const stringsEncounteredAlready = new Set<string>();
    const output: T[] = [];

    for (let item of this) {
        const stringProjection = projection(item);

        if (stringsEncounteredAlready.has(stringProjection)) {
            continue;
        }

        stringsEncounteredAlready.add(stringProjection);
        output.push(item);
    }

    return output;
};

// ===============================================================================

Array.prototype.toDictionary = function <T>(this: Array<T>, keySelector: (obj: T) => string): { [key: string]: T } {
    const dictionary: { [key: string]: T } = {};

    for (let obj of this) {
        const key = keySelector(obj);

        dictionary[key] = obj;
    }

    return dictionary;
}

// ===============================================================================

Array.prototype.firstOrDefault = function <T>(this: Array<T>, predicate: (obj: T) => boolean): (T | null) {
    for (let i = 0; i < this.length; i++) {
        if (predicate(this[i])) {
            return this[i];
        }
    }

    return null;
}

// ===============================================================================

Array.prototype.first = function <T>(this: Array<T>, predicate: (obj: T) => boolean): T {
    for (let i = 0; i < this.length; i++) {
        if (predicate(this[i])) {
            return this[i];
        }
    }

    throw new Error("Element not found");
};

// ===============================================================================

Array.prototype.selectMany = function <T, TSelector>(this: Array<T>, selector: (obj: T) => Array<TSelector>): Array<TSelector> {
    const ret: Array<TSelector> = [];

    for (let item of this) {
        const mapped = selector(item);

        for (let mappedItem of mapped) {
            ret.push(mappedItem);
        }
    }

    return ret;
};

// ===============================================================================

Array.prototype.orderBy = function <T>(this: Array<T>, selector: (obj: T) => string): Array<T> {
    const temp = this.slice();

    temp.sort((a, b) => selector(a).localeCompare(selector(b)));

    return temp;
};

// ===============================================================================

Array.prototype.orderByDescending = function <T>(this: Array<T>, selector: (obj: T) => string): Array<T> {
    const temp = this.slice();

    temp.sort((a, b) => {
        const v = selector(a).localeCompare(selector(b));

        return -v;
    });

    return temp;
};

// ===============================================================================

/**
 * Determines if one string array is equal to another. A boolean enforceOriginalOrder
 * can be passed in to determine if the order of elements in the array should also
 * determine if they are equal.
 */
Array.prototype.equalTo = function (this: Array<string>, a2: Array<string>, enforceOriginalOrder?: boolean): boolean {
    if (this.length !== a2.length) {
        return false;
    }

    let a1ToUse = this;
    let a2ToUse = a2;

    if (!enforceOriginalOrder) {
        a1ToUse = this.orderBy(x => x.toString());
        a2ToUse = a2.orderBy(x => x.toString());
    }

    for (let i = 0; i < a1ToUse.length; ++i) {
        if (a1ToUse[i] !== a2ToUse[i]) {
            return false;
        }
    }

    return true;
};

// ===============================================================================

/**
 * Given an array with possibel duplicate entries, this function returns an array
 * of all duplicate items found. 
 * 
 * For example: 
 *     const a1 = [a,b,b,c];
 *     return a1.getDuplicates(); // [b]
 */
Array.prototype.getDuplicates = function (this: Array<string | number>): Array<string | number> {
    return Array.from(new Set(this.filter((item, index) => this.indexOf(item) !== index)));
}

// ===============================================================================

/**
 * If the source array is <= the threshold length, it is returned joined w/ the join string.
 * If it's > threshold, then a string w/ only the first N items is returned with an appended string of "and K more" where K is the number
 * not included in the string.
 * 
 * Useful for displaying potentially long lists in a summary form in UI
 */
Array.prototype.truncateAndJoinListOfStringsWithCountDescription = function (this: Array<string>, localizer: IStringLocalizer, truncateThreshold: number, joinString: string): string {
    if (this.length > truncateThreshold) {
        return this
            .take(truncateThreshold)
            .concat([localizer.localizeStringFormat("and {0} more", this.length - truncateThreshold)])
            .join(joinString);
    }
    else {
        return this.join(joinString);
    }
};

// ===============================================================================

//
// String extensions
//

String.prototype.trimStartString = function (this: string, startString: string): string {
    if (stringStartsWith(this, startString)) {
        return this.substring(startString.length);
    }
    else {
        return this.valueOf();
    }
}

// ===============================================================================

String.prototype.trimEndString = function (this: string, endString: string): string {
    if (this.endsWith(endString)) {
        return this.substring(0, this.length - endString.length);
    }
    else {
        return this.valueOf();
    }
}

// ===============================================================================

String.prototype.trimChar = function (this: string, charToTrim: string): string {
    let ret = this.toString();

    while ((ret.length > 0) && (ret.charAt(0) === charToTrim)) {
        ret = ret.substring(1);
    }

    while ((ret.length > 0) && (ret.charAt(ret.length - 1) === charToTrim)) {
        ret = ret.substring(0, ret.length - 1);
    }

    return ret;
}

// ===============================================================================

String.prototype.trimChars = function (this: string, charsToTrim: string[]): string {
    let ret = this.toString();

    for (let charToTrim of charsToTrim) {
        ret = ret.trimChar(charToTrim);
    }

    return ret;
}

// ===============================================================================

String.prototype.toDoubleOrDefault = function (this: string, defaultToUse: number): number {
    let s = this;

    const commaSplits = s.split(',');

    if (commaSplits.length == 2) {
        // assume it's an EU style decimal, e.g. 100,23 == 100.23
        s = s.replace(",", ".");
    }
    else {
        // it's comma separated, e.g. "1,000,000" == "1000000"
        s = s.replace(",", "");
    }

    const float = parseFloat(s);

    if (isNaN(float)) {
        return defaultToUse;
    }
    else {
        return float;
    }
}

// ===============================================================================

String.prototype.isDigit = function (this: string): boolean {
    return (this.length > 0) &&
        (this[0] >= '0') &&
        (this[0] <= '9');
}

// ===============================================================================

String.prototype.isWhitespace = function (this: string): boolean {
    return (this.length > 0) && (this.trim().length === 0);
}

// ===============================================================================

String.prototype.emptyCoalesce = function (this: string, coalescedValue: string): string {
    return ((this === null) || (this.length === 0))
        ? coalescedValue
        : this.valueOf();
}

// ===============================================================================

String.prototype.coalesceToMaxSizeAndIndicateTruncationWithEllipsis = function (this: string, maxSize: number): string {
    maxSize = Math.max(maxSize, 3);

    if (this.length <= maxSize) {
        return this.valueOf();
    }

    return this.substring(0, Math.min(this.length, maxSize - 3)) + "...";
}

// ===============================================================================

//
// Number extensions
//

Number.prototype.roundBy = function (this: number, decimalPts: number): number {
    const power = Math.pow(10, decimalPts);

    const val = this * power;

    return (Math.round(val) / power);
}

// ===============================================================================

//
// Helper functions
//

export function copyContentFromHTMLInputElementToClipboard(element: HTMLInputElement) {
    element.select();
    navigator.clipboard.writeText(element.value);
}

// ===============================================================================

export function addhostnameToString(s: string) {
    if (s.length > 0) {
        if (s[0] === "/") {
            s = s.slice(1);
        }
    }

    return `https://${location.host}/${s}`;
}

// ===============================================================================

export function nullOrEmptyCoalesce(s: string | null | undefined, coalesceVal: string){
    if ((s === null) || (s === undefined) || (s.length === 0)) {
        return coalesceVal;
    }

    return s;
}

// ===============================================================================

export function booleanToString(booleanVal: boolean) {
    return booleanVal ? "true" : "false"
}

// ===============================================================================

/**
 * Checks a given string to determine if its a valid email address.
 * Returns true if it is, else returns false.
 * 
 * @param emailAddress the email address to check.
 */
export function isValidEmailAddress(emailAddress: string): boolean {
    emailAddress = emailAddress.trim();

    if (emailAddress.length === 0) {
        return false;
    }

    const pattern = /^([a-z\d!#$%&'*+\-\/=?^_`{|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+(\.[a-z\d!#$%&'*+\-\/=?^_`{|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+)*|"((([ \t]*\r\n)?[ \t]+)?([\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*(([ \t]*\r\n)?[ \t]+)?")@(([a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF][a-z\d\-._~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*[a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])\.)+([a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF][a-z\d\-._~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*[a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])\.?$/i;
    return pattern.test(emailAddress);
}

// ===============================================================================

export function stringStartsWith(source: string, substring: string): boolean {
    if (!source) {
        return false;
    }

    return source.indexOf(substring) === 0;
}

export { }
